import { api } from "./api";

export type Channel = "api" | "b2b" | "social" | "direct";
export type ChangeStatus = "pending" | "approved" | "rejected";

// ─── 成本引擎 occupancy-table（产品按住房人数档成本+售价 · 成本/毛利可能 masked）──
export interface OccupancyRow {
  occupancy: number;
  occupancy_label: string;
  package_cost_per_person_cents: number | null;
  list_price_cents: number;
  company_net_cents: number | null;
  margin_pct: number | null;           // 净毛利率 %（结算价口径，取整；MARKUP-1 起是结果，不是配置）
  room_total_cents: number;
  // MFEE-1 · 非 0 仅当 channel=api——该行挂牌价里含的必付费金额，用于显示「含必付 $X」
  included_mandatory_fee_cents: number;
  // MARKUP-1 · 成本加成率是输入（配置值）；两个毛利率只显示
  markup_pct?: number | null;
  gross_margin_pct?: number | null;    // 毛利率 = (挂牌价 − 成本) ÷ 挂牌价
  net_margin_pct?: number | null;      // 净毛利率 = (结算价 − 成本) ÷ 结算价
  // MARKUP-1b · 本产品实际生效的 commission_pct（全局 + 单产品覆盖）：channel=b2b → 同行建议加价率；channel=api → 平台佣金率
  commission_pct?: number | null;
  // HOTELGATE-1 · 多日游过夜酒店没配齐的晚（非空 = 酒店成本没算齐，价表顶部红条「不能用来报价」）；每行同一份
  overnight_missing?: { night_no: number; line_day: number | null; city: string | null; reason: string }[];
  // PRICEBOOK-1 · 住宿产品才分房；不住宿 = 全部档同价（成本概览悬停标签只对住宿产品弹）
  overnight?: boolean;
}

export function costEngineOccupancyTable(
  productId: string,
  opts?: { pax?: number; channel?: Channel | string; holidayUpliftPct?: number; departureDate?: string; optionId?: string | null },
): Promise<OccupancyRow[]> {
  const qs = new URLSearchParams({ product_id: productId });
  if (opts?.pax) qs.set("pax", String(opts.pax));
  if (opts?.channel) qs.set("channel", String(opts.channel));
  if (opts?.holidayUpliftPct) qs.set("holiday_uplift_pct", String(opts.holidayUpliftPct));
  if (opts?.departureDate) qs.set("departure_date", opts.departureDate);
  if (opts?.optionId) qs.set("option_id", opts.optionId);   // OPT-B2b · 按选项算（不传 = 默认选项）
  return api(`/api/v1/pricing/cost-engine/occupancy-table?${qs.toString()}`);
}

// ─── 成本→渠道售价（口径⑧ · MARKUP-1 成本加成定价法）──
// 结算价 = 成本 × (1 + 成本加成率) × 节假日系数；挂牌价 = 结算价 ÷ (1 − 佣金)。毛利率只显示：(价 − 成本) ÷ 价。
export interface ChannelSellPrice {
  channel: string;
  channel_label: string;
  list_price_cents: number;
  company_net_cents: number | null;
  margin_pct: number | null;           // 净毛利率 %（结算价口径，取整）——结果，不是配置
  commission_cents: number | null;
  // MFEE-1 · 非 0 仅当 channel=api——该行挂牌价里含的必付费金额
  included_mandatory_fee_cents: number;
  markup_pct?: number | null;          // MARKUP-1 · 成本加成率 %（配置值）
  gross_margin_pct?: number | null;    // 毛利率 %（挂牌价口径）
  net_margin_pct?: number | null;      // 净毛利率 %（结算价口径）
}

/** 成本加成率 % → 对应的毛利率 %（只显示用）：m ÷ (1 + m)，结算价口径、节假日系数 1。例：加成 33.33% ≈ 毛利率 25%。 */
export function marginOfMarkup(markupPct: number): number {
  const m = Number(markupPct);
  return Math.round((m / (100 + m)) * 10000) / 100;
}
/** 「加成 33.33% · 毛利率 25%」——加成率在前、毛利率在后。 */
export function markupMarginText(markupPct: number | null | undefined): string {
  if (markupPct == null || Number.isNaN(Number(markupPct))) return "—";
  return `加成 ${+Number(markupPct).toFixed(2)}% · 毛利率 ${+marginOfMarkup(markupPct).toFixed(2)}%`;
}
/** PRICE-CUST-1 · 价格来源标签（只给后台看；同行接口不出） */
export interface PriceSourceTag { level: 1 | 2 | 3 | 4 | 5; label: string }
export interface CostSell {
  price_source?: PriceSourceTag | null;   // 同行价的来源：本产品单独设·固定价 / 本产品单独设 +x% / 跟随全局
  product_id: string;
  product_code: string;
  pax: number;
  occupancy: number;
  vehicle_label: string;
  vehicle_seats: number;
  package_cost_per_person_cents: number | null;
  mandatory_fee_cents: number;
  holiday_uplift_pct: number;
  channels: ChannelSellPrice[];
  notes: string[];
  // CHOVR-1 · 这个产品设过单产品渠道系数（售价总览「单独设置」标记 / 筛选）
  has_channel_override?: boolean;
}
export function costEnginePrice(payload: {
  product_id: string;
  pax: number;
  occupancy?: number;
  holiday_uplift_pct?: number;
  departure_date?: string;
  option_id?: string | null;   // OPT-B2b
}): Promise<CostSell> {
  return api("/api/v1/pricing/cost-engine:price", { method: "POST", body: JSON.stringify(payload) });
}

// ─── CHOVR-1 · 单产品渠道系数（null = 跟随全局 · 保存即生效 · 原因必填）──
export interface OverrideParams {
  markup_pct: number | null;           // MARKUP-1 · 成本加成率 %（原 target_margin_pct）
  commission_pct: number | null;
  net_margin_pct?: number | null;      // 只读 · ≈ 毛利率 %（全局值 / 生效值才带）
}
/** PRICE-CUST-1 · 每个渠道的定价方式：markup 系数 / fixed 固定金额（同行 = 结算价 / 人；散客·社媒 = 售价 / 人；API = 我们到手价 / 人；分 · 多日团按住房档）*/
export type PriceMode = "markup" | "fixed";
export interface FixedFields {
  mode?: PriceMode;
  fixed_price_cents?: number | null;
  fixed_tier_prices?: Record<string, number | null> | null;
  list_price_override_cents?: number | null;
  /** PRICE-LIST-TIER-1 · 同行（多日团）建议售价按住房档 {"1":分,…}；某档缺 = 结算价 ×（1 + 建议加价率） */
  list_tier_prices?: Record<string, number | null> | null;
}
export interface ChannelPrice {
  net_cents: number;
  list_cents: number;
  implied_markup_pct?: number | null;
  net_margin_pct?: number | null;
  gross_margin_pct?: number | null;
}
export interface ChannelOverrideView {
  channel: Channel | string;
  label: string;
  global: OverrideParams;
  override: OverrideParams & FixedFields;
  effective: OverrideParams;
  /** PRICE-CUST-1 · 当前生效价（每档）+ 低于成本差额（分 / 人；没低于 = null） */
  prices?: Record<string, ChannelPrice>;
  net_cents?: number | null;
  list_cents?: number | null;
  below_cost_cents?: number | null;
  below_cost_by_tier?: Record<string, number> | null;
}
export interface ChannelOverridePanel {
  product_id: string;
  product_code: string;
  product_name: string;
  package_cost_per_person_cents: number | null;
  full_pax: number;
  margin_floor_pct: number;
  has_override: boolean;
  channels: ChannelOverrideView[];
  /** PRICE-CUST-1 · 住宿产品 [1,2,3,4]，不住宿 [2]；每档成本 */
  occupancies?: number[];
  cost_by_occupancy?: Record<string, number>;
}
export interface ChannelOverrideInput extends OverrideParams, FixedFields {
  channel: Channel | string;
}
export interface ChannelOverridePreviewRow {
  channel: Channel | string;
  before: { company_net_cents: number | null; list_price_cents: number; net_margin_pct?: number | null; gross_margin_pct?: number | null };
  after: { company_net_cents: number | null; list_price_cents: number; net_margin_pct?: number | null; gross_margin_pct?: number | null };
  margin_floor_breached: boolean;
  below_cost_cents?: number | null;   // PRICE-CUST-1 · 改后到手价低于成本的差额（> 0 才有）
  below_cost_by_tier?: Record<string, number> | null;
  after_by_occupancy?: Record<string, { company_net_cents: number; list_price_cents: number }>;
}
export interface ChannelOverridePreview {
  product_id: string;
  margin_floor_pct: number;
  occupancies?: number[];
  channels: ChannelOverridePreviewRow[];
}
export interface ChannelOverrideChange {
  id: string;
  at: string;
  type: "product_channel_override";
  type_label: string;
  actor_name: string;
  actor_role: string | null;
  product_id: string;
  product_code: string | null;
  product_name: string | null;
  changes: string[];
  reason: string | null;
  reason_code?: string | null;
}
const overrideUrl = (productId: string) => `/api/v1/pricing/products/${productId}/channel-overrides`;
export function getChannelOverrides(productId: string): Promise<ChannelOverridePanel> {
  return api(overrideUrl(productId));
}
export function previewChannelOverrides(productId: string, overrides: ChannelOverrideInput[]): Promise<ChannelOverridePreview> {
  return api(`${overrideUrl(productId)}:preview`, { method: "POST", body: JSON.stringify({ overrides }) });
}
/** 保存：值真变了才要原因（REASON-1 代码 / 补充说明至少一个）；低于成本时补充说明必填。 */
export function saveChannelOverrides(
  productId: string, overrides: ChannelOverrideInput[], reason: { reason_code?: string; reason?: string },
): Promise<ChannelOverridePanel> {
  return api(overrideUrl(productId), { method: "PUT", body: JSON.stringify({ overrides, ...reason }) });
}
export function listChannelOverrideChanges(limit = 100): Promise<ChannelOverrideChange[]> {
  return api(`/api/v1/pricing/channel-override-changes?limit=${limit}`);
}

/**
 * MARKUP-1b · 同行建议零售价的口径小标：「结算价 + 17% 加价」。
 * pct 必须是本产品**实际生效**的同行建议加价率（occupancy-table 行的 ``commission_pct``——后端用算价同一个 load_channels：全局 + 单产品覆盖），
 * 不读全局默认；没拿到 → 「结算价 + 加价」（不带数字，不编）。
 */
export function b2bRetailTag(pct: number | null | undefined): string {
  return pct == null ? "结算价 + 加价" : `结算价 + ${+Number(pct).toFixed(2)}% 加价`;
}

/** 价格口径说明小字（同行 / API 两种挂牌价算法不同）。 */
export const RETAIL_FORMULA_NOTE = "同行建议零售价 = 同行结算价 × (1 + 同行建议加价率)；API 平台价 = 结算价 ÷ (1 − 平台佣金率)";

// ─── 成本拆解（车摊/酒店/门票 · :calculate · 成本可见角色）──
export interface CostBreakdown {
  product_id: string;
  product_code: string;
  pax: number;
  occupancy: number;
  total_days: number;
  total_nights: number;
  vehicle_label: string;
  vehicle_seats: number;
  fleet_daily_cost_cents: number;
  fleet_per_person_cents: number;
  hotel_per_person_cents: number;
  ticket_total_cents: number;        // 门票过路账·每人（只含自费 / 必付景点，每景点一张成人票·同景点多票种取最高；「已含」的票在套餐成本里不重复算）
  package_cost_per_person_cents: number;
  mandatory_fee_cents: number;
  markup_pct: number;                // MARKUP-1 · 建议价用的成本加成率%（默认 30；原 target_margin_pct）
  suggested_price_per_person_cents: number;   // 建议价 = 成本 × (1 + 加成率)
  notes: string[];
  // TRANSCOST-1 · 大车天数 + 接送机成本（每趟全包·一车一趟一个价；产品成本表按每趟 ÷ 2 人估）
  bus_days?: number;
  transfer_per_person_cents?: number;
  transfer_pickup_cents?: number | null;
  transfer_dropoff_cents?: number | null;
  transfer_pickup_source?: "none" | "product" | "line" | "unset";
  transfer_dropoff_source?: "none" | "product" | "line" | "unset";
  transfer_trip_total_cents?: number;
  transfer_pax?: number;
  transfer_cost_missing?: boolean;
  // COSTADJ-1 · 午餐 + 车队日成本单独调整
  lunch_days?: number;
  lunch_per_person_cents?: number;
  lunch_cost_missing?: boolean;
  // INTERCITY-1 · 城际用车（按人）：每段线路 / 方向 / 算式 / 金额
  intercity_per_person_cents?: number;
  intercity_legs?: { route_id: string; route_name_cn: string; route_name_en: string; direction: IntercityDirection; one_way_cents: number; amount_cents: number; formula: string }[];
  fleet_daily_source?: "vehicle" | "product_override";
  fleet_daily_vehicle_cents?: number;   // 车辆资料原值（单独调整时画删除线）
  // TIXSEL-1 · 已含门票（选了票种的进套餐成本）：明细 + 没选票的红字
  ticket_included_per_person_cents?: number;
  included_tickets?: { attraction_id: string; attraction_name: string; ticket_type_id: string; ticket_name: string; unit_cents: number }[];
  ticket_cost_missing?: boolean;
  ticket_missing_names?: string[];
}
export function costEngineCalculate(payload: {
  product_id: string;
  pax: number;
  occupancy?: number;
  markup_pct?: number;
  option_id?: string | null;   // OPT-B2b
}): Promise<CostBreakdown> {
  return api("/api/v1/pricing/cost-engine:calculate", { method: "POST", body: JSON.stringify(payload) });
}

// ─── 保本红黄绿（按团人数 N）──
export interface BreakevenRow {
  pax: number;
  package_cost_per_person_cents: number | null;
  margin_pct: number | null;
  profit_per_person_cents: number | null;
  status: string;
  vehicle_label: string;   // BKEVEN-1 · 该行实际用的车型（表头用）
  vehicle_seats: number;
}
export function costEngineBreakeven(
  productId: string,
  opts?: { occupancy?: number; minPax?: number; maxPax?: number; vehicleModel?: string; optionId?: string | null },
): Promise<BreakevenRow[]> {
  const qs = new URLSearchParams({ product_id: productId });
  if (opts?.occupancy) qs.set("occupancy", String(opts.occupancy));
  if (opts?.minPax) qs.set("min_pax", String(opts.minPax));
  if (opts?.maxPax) qs.set("max_pax", String(opts.maxPax));
  if (opts?.vehicleModel) qs.set("vehicle_model", opts.vehicleModel);
  if (opts?.optionId) qs.set("option_id", opts.optionId);   // OPT-B2b
  return api(`/api/v1/pricing/cost-engine/breakeven?${qs.toString()}`);
}

// ─── BKEVEN-1 · 保本按车型分 Tab——产品可选车型列表 ──
export interface VehicleOption {
  model_label_cn: string;
  seat_capacity: number;
  passenger_capacity: number;
  daily_cost_cents_min: number | null;  // 非成本可见角色 = null（口径⑦）
  active_count: number;
}
export function costEngineVehicleOptions(productId: string): Promise<VehicleOption[]> {
  const qs = new URLSearchParams({ product_id: productId });
  return api(`/api/v1/pricing/cost-engine/vehicle-options?${qs.toString()}`);
}

// ─── 渠道系数配置（口径⑧·DB）──
export interface ChannelConfig {
  channel: string;
  label: string;
  markup_pct: number | null;       // MARKUP-1 · 成本加成率 %（原 target_margin_pct，数值沿用）；RBACFIX-2 · 没有「成本查看」权限 = null
  net_margin_pct: number | null;   // ≈ 毛利率 %（只读 = 加成 ÷ (1 + 加成)）；同上
  commission_pct: number;
  sort_order: number;
  is_active: boolean;
}
export function listChannelConfig(): Promise<ChannelConfig[]> {
  return api("/api/v1/pricing/channel-config");
}
export function updateChannelConfig(
  channel: string,
  payload: { markup_pct?: number; commission_pct?: number; is_active?: boolean; reason_code?: string; reason?: string },   // REASON-1 · 加成率 / 佣金真的变了必带原因
): Promise<ChannelConfig> {
  return api(`/api/v1/pricing/channel-config/${channel}`, { method: "PATCH", body: JSON.stringify(payload) });
}

// ─── PRICEBOOK-1 · 同行价目表矩阵：全部产品 × 1–4 人一房，数据来自成本引擎（取代旧 b2b_price_book）──
export interface PriceBookMatrixCell {
  net_cents: number;                 // 同行结算价
  list_cents: number;                // 同行建议售价
  net_source: "fixed" | "auto";      // fixed = 本产品设了固定金额；auto = 系数算出
  list_source: "fixed" | "auto";
}
/** OPTPRICE-1 · 价目表每个非默认选项一行：same_as_product = 没单独定价（页面标「同产品」）；否则 cells 同产品逐档 */
export interface PriceBookMatrixOption {
  option_id: string;
  code: string;
  name_cn: string | null;
  same_as_product: boolean;
  error?: string;
  cells?: Record<string, PriceBookMatrixCell>;
}
export interface PriceBookMatrixProduct {
  product_id: string;
  options?: PriceBookMatrixOption[];
  code: string;
  name_cn: string | null;
  status: string;
  error?: string;
  multi?: boolean;                   // true = 住宿产品（1–4 人一房逐档）；false = 不分房只有一档（2）
  occupancies?: number[];
  cells?: Record<string, PriceBookMatrixCell>;
  override?: {
    mode: "markup" | "fixed";
    fixed_price_cents: number | null;
    fixed_tier_prices: Record<string, number | null> | null;
    list_price_override_cents: number | null;
    list_tier_prices: Record<string, number | null> | null;
    markup_pct: number | null;       // 改价时要把现有系数原样带回去；没有成本查看权限时为 null 且 editable=false
    commission_pct: number | null;
  };
}
export interface PriceBookMatrix { editable: boolean; products: PriceBookMatrixProduct[] }
export function getPriceBookMatrix(): Promise<PriceBookMatrix> {
  return api("/api/v1/pricing/price-book-matrix");
}

// ─── 旧同行价目表（已弃用 · PRICEBOOK-1：页面不再读写；端点保留）──
export interface PriceBookRow {
  occupancy: number;
  occupancy_label: string;
  list_price_cents: number | null;  // null = 待填价（B 段·尚未人工填）
}
/** @deprecated PRICEBOOK-1 · 用 getPriceBookMatrix */
export function getPriceBook(productId: string): Promise<PriceBookRow[]> {
  return api(`/api/v1/pricing/price-book?product_id=${productId}`);
}
export function generatePriceBook(payload: {
  product_id: string;
  pax?: number;
}): Promise<PriceBookRow[]> {
  return api("/api/v1/pricing/price-book:generate", { method: "POST", body: JSON.stringify(payload) });
}
/** 手动调价 upsert（同行价目表·全年统一价）。list_price_cents=null 清空回待填价。 */
export function setPriceBook(payload: {
  product_id: string;
  rows: { occupancy: number; list_price_cents: number | null }[];
  reason_code?: string;   // REASON-1
  reason?: string;
}): Promise<PriceBookRow[]> {
  return api("/api/v1/pricing/price-book", { method: "PUT", body: JSON.stringify(payload) });
}

export interface ChannelMarkup {
  channel: Channel;
  channel_label: string;
  markup_pct: number;
  tax_included: boolean;
  fee_included: boolean;
  last_updated_at: string;
  pending_change_id: string | null;
}

export interface B2BOverride {
  id: string;
  partner_id: string;
  partner_name: string;
  product_id: string | null;
  product_name: string | null;
  override_pct: number;
  note: string | null;
  effective_at: string;
  expires_at: string | null;
}

export interface PricingChangeRequest {
  id: string;
  target_kind: "channel_markup" | "b2b_override";
  target_ref: string;
  current_pct: number;
  proposed_pct: number;
  delta_pct: number;
  reason: string;
  status: ChangeStatus;
  submitted_by: string;
  submitted_at: string;
  decided_by: string | null;
  decided_at: string | null;
  decision_note: string | null;
}

export interface PricingDashboard {
  channel_markups: ChannelMarkup[];
  b2b_overrides_count: number;
  pending_changes_count: number;
  profit_top5: { product: string; gmv_cents: number; profit_pct: number }[];
  anomaly_alerts: { product: string; alert: string; severity: string }[];
}

export const CHANNEL_LABEL: Record<Channel, string> = {
  api: "API 渠道",
  b2b: "同业 B2B",
  social: "社媒带货",
  direct: "自主独立站",
};

// W4 · channels / customer-overrides / holidays / preview
export interface ChannelUpdatePayload {
  markup_pct: number;
  reason?: string;
  tax_included?: boolean;
  fee_included?: boolean;
}

export interface ChannelUpdateResult {
  applied: boolean;
  channel: Channel;
  new_markup_pct: number | null;
  change_request_id: string | null;
  requires_owner_approval: boolean;
  delta_pct: number;
}

export interface CustomerOverride {
  id: string;
  partner_id: string;
  partner_name: string;
  discount_pct: number;
  note: string | null;
  effective_from: string;
  effective_to: string | null;
  created_at: string;
}

export interface CustomerOverrideCreate {
  partner_id: string;
  discount_pct: number;
  note?: string;
  effective_from?: string;
  effective_to?: string;
}

export interface HolidayConfig {
  id: string;
  name: string;
  date_start: string;
  date_end: string;
  uplift_pct: number;
  note: string | null;
  created_at: string;
}

export interface HolidayConfigCreate {
  name: string;
  date_start: string;
  date_end: string;
  uplift_pct: number;
  note?: string;
}

export interface PreviewRequest {
  base_cents: number;
  channel: Channel;
  partner_id?: string;
  target_date?: string;
}

export interface PreviewResponse {
  base_cents: number;
  channel_markup_pct: number;
  holiday_uplift_pct: number;
  customer_discount_pct: number;
  final_cents: number;
  formula: string;
  breakdown: { step: string; label?: string; value_cents: number; factor: string; pct?: number }[];
}

export const D3B_THRESHOLD_PCT = 30.0;

// ─── T-ALIGN-02 · pricing:overview 类型 ────────────────────────────────────

/** canvas 4 枚举（policy_type 统一出口） */
export type PricePolicyType =
  | "self_contract"
  | "hold_allotment"
  | "api_direct"
  | "crawled_retail";

export const POLICY_META: Record<
  PricePolicyType,
  { labelCn: string; pillBg: string; pillText: string; dot: string }
> = {
  self_contract: {
    labelCn: "合约直签",
    pillBg: "bg-emerald-100",
    pillText: "text-emerald-700",
    dot: "bg-emerald-500",
  },
  hold_allotment: {
    labelCn: "Hold 留位",
    pillBg: "bg-blue-100",
    pillText: "text-blue-700",
    dot: "bg-blue-500",
  },
  api_direct: {
    labelCn: "API 实时",
    pillBg: "bg-violet-100",
    pillText: "text-violet-700",
    dot: "bg-violet-500",
  },
  crawled_retail: {
    labelCn: "抓取零售",
    pillBg: "bg-slate-100",
    pillText: "text-slate-700",
    dot: "bg-slate-400",
  },
};

export interface SeasonHeatmapCell {
  quarter: string;      // "Q1 淡季" / "Q2 平" / "Q3 旺季" / "Q4 平"
  base_cents: number;
  mod_pct: number;      // 0 when no date-segmented data (vehicle/attraction)
}

export interface SeasonHeatmapRow {
  resource_kind: "hotel" | "vehicle" | "attraction";
  resource_kind_cn: string;
  cells: SeasonHeatmapCell[];
}

export interface ResourcePolicyItem {
  id: string;
  resource_kind: "hotel" | "vehicle" | "attraction";
  resource_name_cn: string;
  policy_type: PricePolicyType;
  supplier_code: string;
  min_price_cents: number;
  max_price_cents: number;
  active_days: number;
  last_change_at: string | null;
}

export interface PricingOverviewStats {
  total_policies: number;
  avg_price_cents: number;
  changes_30d: number;
  anomaly_count: number;
}

export interface PricingOverviewResponse {
  stats: PricingOverviewStats;
  season_heatmap: SeasonHeatmapRow[];
  resource_policies: ResourcePolicyItem[];
}

// ─── INTERCITY-1 · 城际用车价目（财务维护；产品里只能下拉选）──
export type IntercityDirection = "round_trip" | "a_to_b" | "b_to_a";
export const INTERCITY_DIRECTION_LABEL: Record<IntercityDirection, string> = { round_trip: "往返", a_to_b: "去程", b_to_a: "回程" };
export interface IntercityRoute {
  id: string;
  city_a_cn: string; city_a_en: string; city_b_cn: string; city_b_en: string;
  name_cn: string; name_en: string;
  one_way_cents: number;
  round_trip_cents: number;
  status: "active" | "inactive";
  in_use_products: number;
  updated_by_name?: string | null;
  updated_at?: string | null;
}
export interface IntercityRouteInput {
  city_a_cn: string; city_a_en: string; city_b_cn: string; city_b_en: string;
  one_way_cents: number;
  status?: "active" | "inactive";
}
export interface IntercityUsage {
  route_id: string;
  old_one_way_cents: number;
  new_one_way_cents: number;
  products: { product_id: string; code: string; name_cn: string; direction: IntercityDirection; direction_label: string; old_amount_cents: number; new_amount_cents: number }[];
}
/** 一段城际用车的金额 / 人：往返 = 单程 × 2，去程 / 回程 = 单程 × 1（只用于下拉框旁的预览；真金额以后端返回的 amount_cents 为准）。 */
export const intercityAmount = (oneWayCents: number, d: IntercityDirection) => oneWayCents * (d === "round_trip" ? 2 : 1);
/** 方向下拉的文案：往返 / 去程：A → B / 回程：B → A。 */
export const intercityDirectionText = (r: Pick<IntercityRoute, "city_a_cn" | "city_b_cn">, d: IntercityDirection) =>
  d === "round_trip" ? "往返" : d === "a_to_b" ? `去程：${r.city_a_cn} → ${r.city_b_cn}` : `回程：${r.city_b_cn} → ${r.city_a_cn}`;

export const intercityApi = {
  list: (status?: "active" | "inactive") => api<IntercityRoute[]>(`/api/v1/pricing/intercity-routes${status ? `?status=${status}` : ""}`),
  create: (payload: IntercityRouteInput) => api<IntercityRoute>("/api/v1/pricing/intercity-routes", { method: "POST", body: JSON.stringify(payload) }),
  update: (id: string, payload: Partial<IntercityRouteInput>) =>
    api<IntercityRoute>(`/api/v1/pricing/intercity-routes/${id}`, { method: "PATCH", body: JSON.stringify(payload) }),
  usage: (id: string, newOneWayCents?: number) =>
    api<IntercityUsage>(`/api/v1/pricing/intercity-routes/${id}/usage${newOneWayCents != null ? `?new_one_way_cents=${newOneWayCents}` : ""}`),
};

export const pricingApi = {
  dashboard: () => api<PricingDashboard>("/api/v1/pricing/dashboard"),
  listMarkups: () => api<ChannelMarkup[]>("/api/v1/pricing/channel-markups"),
  // W4
  listChannels: () => api<ChannelMarkup[]>("/api/v1/pricing/channels"),
  updateChannel: (channel: Channel, payload: ChannelUpdatePayload) =>
    api<ChannelUpdateResult>(`/api/v1/pricing/channels/${channel}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    }),
  listCustomerOverrides: (partnerId?: string) =>
    api<CustomerOverride[]>(
      `/api/v1/pricing/customer-overrides${partnerId ? `?partner_id=${partnerId}` : ""}`,
    ),
  createCustomerOverride: (payload: CustomerOverrideCreate & { reason_code?: string; reason?: string }) =>
    api<CustomerOverride>("/api/v1/pricing/customer-overrides", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  deleteCustomerOverride: (id: string) =>
    api<void>(`/api/v1/pricing/customer-overrides/${id}`, { method: "DELETE" }),
  listHolidays: () => api<HolidayConfig[]>("/api/v1/pricing/holidays"),
  createHoliday: (payload: HolidayConfigCreate & { reason_code?: string; reason?: string }) =>
    api<HolidayConfig>("/api/v1/pricing/holidays", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  // REASON-2 · 删节假日加价：原因选填（带了进审计）
  deleteHoliday: (id: string, reason?: { reason_code?: string; reason?: string }) =>
    api<void>(`/api/v1/pricing/holidays/${id}${reason?.reason_code || reason?.reason ? `?${new URLSearchParams({ ...(reason.reason_code ? { reason_code: reason.reason_code } : {}), ...(reason.reason ? { reason: reason.reason } : {}) })}` : ""}`, { method: "DELETE" }),
  preview: (payload: PreviewRequest) =>
    api<PreviewResponse>("/api/v1/pricing/preview", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  listOverrides: () => api<B2BOverride[]>("/api/v1/pricing/overrides"),
  createOverride: (payload: {
    partner_id: string;
    product_id?: string | null;
    override_pct: number;
    note?: string;
    reason_code?: string;   // REASON-1
    reason?: string;
  }) =>
    api<B2BOverride>("/api/v1/pricing/overrides", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  listChanges: (status?: ChangeStatus) =>
    api<PricingChangeRequest[]>(
      `/api/v1/pricing/change-requests${status ? `?status_filter=${status}` : ""}`,
    ),
  submitChange: (payload: {
    target_kind: "channel_markup" | "b2b_override";
    target_ref: string;
    proposed_pct: number;
    reason: string;
    reason_code?: string;   // REASON-1 · 提申请必带原因代码
  }) =>
    api<PricingChangeRequest>("/api/v1/pricing/change-requests", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  decideChange: (rid: string, approve: boolean, note?: string) =>
    api<PricingChangeRequest>(
      `/api/v1/pricing/change-requests/${rid}/approve?approve=${approve}${
        note ? `&note=${encodeURIComponent(note)}` : ""
      }`,
      { method: "PATCH" },
    ),

  /** T-ALIGN-02 · 三段聚合：stats / season_heatmap / resource_policies */
  overview: () => api<PricingOverviewResponse>("/api/v1/pricing/:overview"),
};
